import dayjs, { ConfigType as DayjsConfigType } from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import utc from "dayjs/plugin/utc";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import {
    CustomObjectProvisioningState,
    ExecutionStatus as ExecutionStatusType,
} from "graphql/generated/graphql";
import { isObject } from "lodash";

export const convertTime = (timeInMs: number) => {
    const hours = Math.floor(timeInMs / 3600);
    const minutes = Math.floor((timeInMs % 3600) / 60);
    const seconds = hours ? 0 : Math.floor((timeInMs % 3600) % 60);
    const milliseconds = timeInMs < 1 ? Math.floor(timeInMs * 1000) : 0;

    const hoursResult = hours > 0 ? `${hours} ${hours === 1 ? " hour " : " hours "}` : "";
    const minutesResult = minutes > 0 ? `${minutes} ${minutes === 1 ? "minute " : "minutes "}` : "";
    const secondsResult = seconds > 0 ? `${seconds} ${seconds === 1 ? "second " : "seconds "}` : "";
    const milliSecondsResult =
        milliseconds > 0
            ? `${milliseconds} ${milliseconds === 1 ? "millisecond" : "milliseconds"}`
            : "";

    return hoursResult + minutesResult + secondsResult + milliSecondsResult;
};

export enum ExecutionStatus {
    WAITING_DEPENDENCY = "WAITING_DEPENDENCY",
    WAITING_LOCK = "WAITING_LOCK",
    WAITING_RECORD = "WAITING_RECORD",
    WAITING_ENGINE = "WAITING_ENGINE",
    WAITING_RESPONSE = "WAITING_RESPONSE",
    AT_ENGINE = "AT_ENGINE",
    ENDED_SUCCESS = "ENDED_SUCCESS",
    ENDED_ERROR = "ENDED_ERROR",
    ENDED_CANCELLED = "ENDED_CANCELLED",
    PAUSED = "PAUSED",
    SCHEDULED = "SCHEDULED",
}

export const getCustomObjectProvisioningStateColors = (
    provisioningState: CustomObjectProvisioningState
) => {
    if (!provisioningState) {
        return;
    }
    if (provisioningState.match(/ing$/i)) {
        return "light-blue";
    } else if (provisioningState.match(/._failed/i)) {
        return "primary-red";
    } else if (provisioningState.match(/deleted/i)) {
        return "primary-yellow";
    } else if (provisioningState.match(/ready/i)) {
        return "primary-green";
    }
};

/**
 *
 * @param timeToConvert string in ISO format with UTS timezone
 * @returns human readable format in local timezone
 */
export const getLocalTimezoneDate = (timeToConvert: string, format?: string) => {
    dayjs.extend(customParseFormat);
    return dayjs(timeToConvert).format(format ?? "YYYY-MM-DD HH:mm:ss.SSS Z");
};

/**
 *
 * @param timeToConvert string
 * @returns time in local timezone format
 */
export const getLocalTime = (timeToConvert: string, include_utcoffset: boolean) => {
    dayjs.extend(utc);
    dayjs.extend(customParseFormat);
    if (!timeToConvert) return "";

    const parsedTime = dayjs(
        timeToConvert,
        include_utcoffset ? "HH:mm:ssZ" : ["HH:mm:ss", "HH:mm"],
        true
    );
    if (!parsedTime.isValid()) return "Invalid Date";

    const localTime = parsedTime.local();
    // Display HH:mm when seconds are zero (the common case: the picker assumes
    // :00 seconds); only fall back to HH:mm:ss when a non-zero seconds value is
    // actually present.
    return localTime.format(localTime.second() === 0 ? "HH:mm" : "HH:mm:ss");
};

/**
 *
 * @param timeToConvert string
 * @returns string in "YYYY-MM-DDTHH:mm:ss.SSSZ" format
 */
export const getLocalTimeZoneISO = (timeToConvert: string) =>
    getLocalTimezoneDate(timeToConvert, "YYYY-MM-DDTHH:mm:ss.SSSZ");

const isMomentObj = (timeToConvert: any) => {
    return isObject(timeToConvert) && (timeToConvert as any)._isAMomentObject;
};
export const convertMomentObjToDayjs = (timeToConvert: any) => {
    if (!timeToConvert) return "";
    if (isMomentObj(timeToConvert)) {
        return dayjs(timeToConvert.toISOString());
    } else {
        return timeToConvert;
    }
};

/**
 *
 * @param timeToConvert DayJs object
 * @param options isOnlyTime, isOnlyDate, include_utcoffset boolean
 * @returns string in local "HH:mm:ssZ", "HH:mm:ss", "YYYY-MM-DDTHH:mm:ssZ" or "YYYY-MM-DDTHH:mm:ss" format
 */
export const getUpdatedTime = (
    timeToConvert: DayjsConfigType,
    options: {
        isOnlyTime?: boolean;
        isOnlyDate?: boolean;
        include_utcoffset?: boolean;
    } = {}
) => {
    dayjs.extend(utc);
    dayjs.extend(customParseFormat);
    dayjs.extend(customParseFormat);
    dayjs.extend(advancedFormat);
    dayjs.extend(weekday);
    dayjs.extend(localeData);
    dayjs.extend(weekOfYear);
    dayjs.extend(weekYear);

    const { isOnlyTime, isOnlyDate, include_utcoffset } = options;

    const timeToConvertDayjs = convertMomentObjToDayjs(timeToConvert);

    const offsetMinutes = dayjs().utcOffset();
    const format = isOnlyTime ? "HH:mm" : "YYYY-MM-DDTHH:mm";
    const localTime = dayjs(timeToConvertDayjs, format);
    if (!localTime.isValid()) return "Invalid Date";

    const timeWithOffset = localTime.utcOffset(offsetMinutes);
    return isOnlyDate
        ? timeWithOffset.format("YYYY-MM-DD")
        : isOnlyTime
        ? include_utcoffset
            ? timeWithOffset.format("HH:mm:ssZ")
            : // Store full HH:mm:ss (seconds assumed :00) so the value satisfies
              // the JSON-schema `time` format, which requires seconds.
              timeWithOffset.format("HH:mm:ss")
        : include_utcoffset
        ? timeWithOffset.format("YYYY-MM-DDTHH:mm:ssZ")
        : timeWithOffset.format("YYYY-MM-DDTHH:mm:ss");
};

/**
 *
 * @param timeToConvert string
 * @returns string in "YYYY-MM-DD HH:mm:ss.SSS" format
 */
export const getLocalDateTime = (timeToConvert: string, format?: string) =>
    getLocalTimezoneDate(timeToConvert, format ?? "YYYY-MM-DD HH:mm:ss.SSS");

export const getExecutionMode = (data) => {
    if (!data) {
        return;
    }
    const { is_productive, use_breakpoints, use_only_explicit_breakpoints } = data;

    if (is_productive) {
        return;
    }

    if (use_breakpoints === true) {
        if (use_only_explicit_breakpoints === true) {
            return "Debug Mode (fixed breakpoints)";
        } else {
            return "Debug Mode";
        }
    } else {
        return "Dev Mode";
    }
};

export const hasExecutionEndedStatus = (status: ExecutionStatusType) => {
    return status === "ENDED_ERROR" || status === "ENDED_SUCCESS" || status === "ENDED_CANCELLED";
};
export const hasExecutionEndedOrPausedStatus = (status: ExecutionStatusType) => {
    return (
        status === "ENDED_ERROR" ||
        status === "ENDED_SUCCESS" ||
        status === "ENDED_CANCELLED" ||
        status === "PAUSED"
    );
};
